import { Schema } from 'effect';

import { DateTimeSerializableFromString } from '../DateTimeSerializableFromString';
import { overrideSchema } from '../openapi/overrideSchema';

/**
 * Order fields whose generated closure is deliberately supplied by a tenant.
 * Cart detail reaches customer, product, adjustment, payment, and shipment
 * trees; the tenant app's `orderTightened.ts` layers these brands over the
 * exact generated roots instead of importing that 1,121-export closure here.
 */
export type OrderOverrideSlots =
  | 'OrderItem.variant'
  | 'OrderDetail.promotionRuleHints'
  | 'OrderDetail.customer'
  | 'OrderDetail.channel'
  | 'OrderDetail.shippingAddress'
  | 'OrderDetail.billingAddress'
  | 'OrderDetail.payments'
  | 'OrderDetail.shipments'
  | 'OrderDetail.promotionCoupon'
  | 'OrderDetailPaid.promotionRuleHints'
  | 'OrderDetailPaid.customer'
  | 'OrderDetailPaid.channel'
  | 'OrderDetailPaid.shippingAddress'
  | 'OrderDetailPaid.billingAddress'
  | 'OrderDetailPaid.payments'
  | 'OrderDetailPaid.shipments'
  | 'OrderDetailPaid.promotionCoupon'
  | 'OrderDetailPaid.documents'
  | 'OrderIndex.shipments'
  | 'OrderIndex.items';

const JsonLdContext = Schema.optionalKey(
  Schema.Union(
    [
      Schema.String,
      Schema.Struct({
        '@vocab': Schema.String,
        hydra: Schema.Literal('http://www.w3.org/ns/hydra/core#'),
      }),
    ],
    { mode: 'oneOf' }
  ).annotate({ readOnly: true })
);
const JsonLdIri = Schema.optionalKey(Schema.String.annotate({ readOnly: true }));
const JsonLdType = Schema.optionalKey(Schema.String.annotate({ readOnly: true }));
const NullableString = Schema.Union([Schema.String, Schema.Null]);
const ReadOnlyInt = Schema.optionalKey(
  Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt())
);
const ReadOnlyMoney = Schema.optionalKey(
  Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt())
);
const ReadOnlyNullableMoney = Schema.optionalKey(
  Schema.Union([Schema.Finite.check(Schema.isInt()), Schema.Null]).annotate({ readOnly: true })
);

const OrderIri = Schema.optionalKey(
  Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('Order.iri'))
);

/** Branded order token for route and payment boundaries. */
export const OrderToken = Schema.String.pipe(Schema.brand('Order.tokenValue'));
export type OrderToken = typeof OrderToken.Type;

const OrderTokenValue = Schema.optionalKey(
  Schema.Union([OrderToken, Schema.Null]).annotate({ readOnly: true })
);
const OrderNumber = Schema.Union([Schema.String.pipe(Schema.brand('Order.number')), Schema.Null]);
const OrderState = Schema.String.annotate({ default: 'cart' }).pipe(Schema.brand('Order.state'));
const OrderCheckoutState = Schema.String.annotate({ default: 'cart' }).pipe(
  Schema.brand('Order.checkoutState')
);
const OrderPaymentState = Schema.String.annotate({ default: 'cart' }).pipe(
  Schema.brand('Order.paymentState')
);
const OrderShippingState = Schema.String.annotate({ default: 'cart' }).pipe(
  Schema.brand('Order.shippingState')
);
const OrderGlobalStateValues = Schema.Literals([
  'AcceptedForPayment',
  'Paid',
  'AcceptedForProcessing',
  'Processing',
  'Resolved',
  'ResolvedStore',
  'ResolvedWithoutEmail',
  'Cancelled',
  'TopolPickup',
  'PardubicePickup',
  'NotPaidEmail',
  'NotPaidCancelled',
  'NotPickedUp',
]);
const OrderGlobalState = Schema.optionalKey(
  Schema.Union([
    OrderGlobalStateValues.pipe(Schema.brand('Order.globalState')),
    Schema.Null,
  ]).annotate({ readOnly: true })
);

const OrderReferenceOverrides = {
  tokenValue: OrderTokenValue,
  number: OrderNumber,
};
const OrderLifecycleOverrides = {
  state: OrderState,
  checkoutState: OrderCheckoutState,
  paymentState: OrderPaymentState,
  shippingState: OrderShippingState,
  globalState: OrderGlobalState,
};

export const OrderItemBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  /** Override slot: exact tenant product-variant tree. */
  variant: Schema.Unknown,
  productName: NullableString,
  id: ReadOnlyInt,
  quantity: Schema.Finite.annotate({ readOnly: true })
    .check(Schema.isInt())
    .check(Schema.isGreaterThanOrEqualTo(0)),
  unitPrice: Schema.Finite.check(Schema.isInt()),
  discountedUnitPrice: ReadOnlyMoney,
  frontUnitPriceWithVat: ReadOnlyNullableMoney,
  frontItemsPriceWithVat: ReadOnlyNullableMoney,
  frontUnitPriceWithoutVatAfterDiscount: ReadOnlyMoney,
  frontItemsPriceWithVatAfterDiscount: ReadOnlyNullableMoney,
  frontItemsPriceWithoutVatAfterDiscount: ReadOnlyMoney,
});

export const OrderItem = overrideSchema(OrderItemBase, {
  '@id': Schema.optionalKey(
    Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('OrderItem.iri'))
  ),
  id: Schema.optionalKey(
    Schema.Finite.annotate({ readOnly: true })
      .check(Schema.isInt())
      .pipe(Schema.brand('OrderItem.id'))
  ),
  quantity: Schema.Finite.annotate({ readOnly: true })
    .check(Schema.isInt())
    .check(Schema.isGreaterThanOrEqualTo(0))
    .pipe(Schema.brand('OrderItem.quantity')),
  unitPrice: Schema.Finite.check(Schema.isInt()).pipe(Schema.brand('OrderItem.unitPrice')),
  discountedUnitPrice: Schema.optionalKey(
    Schema.Finite.annotate({ readOnly: true })
      .check(Schema.isInt())
      .pipe(Schema.brand('OrderItem.discountedUnitPrice'))
  ),
  frontUnitPriceWithVat: Schema.optionalKey(
    Schema.Union([
      Schema.Finite.check(Schema.isInt()).pipe(Schema.brand('OrderItem.frontUnitPriceWithVat')),
      Schema.Null,
    ]).annotate({ readOnly: true })
  ),
  frontItemsPriceWithVat: Schema.optionalKey(
    Schema.Union([
      Schema.Finite.check(Schema.isInt()).pipe(Schema.brand('OrderItem.frontItemsPriceWithVat')),
      Schema.Null,
    ]).annotate({ readOnly: true })
  ),
  frontUnitPriceWithoutVatAfterDiscount: Schema.optionalKey(
    Schema.Finite.annotate({ readOnly: true })
      .check(Schema.isInt())
      .pipe(Schema.brand('OrderItem.frontUnitPriceWithoutVatAfterDiscount'))
  ),
  frontItemsPriceWithVatAfterDiscount: Schema.optionalKey(
    Schema.Union([
      Schema.Finite.check(Schema.isInt()).pipe(
        Schema.brand('OrderItem.frontItemsPriceWithVatAfterDiscount')
      ),
      Schema.Null,
    ]).annotate({ readOnly: true })
  ),
  frontItemsPriceWithoutVatAfterDiscount: Schema.optionalKey(
    Schema.Finite.annotate({ readOnly: true })
      .check(Schema.isInt())
      .pipe(Schema.brand('OrderItem.frontItemsPriceWithoutVatAfterDiscount'))
  ),
}).annotate({ identifier: 'OrderItem' });
export type OrderItem = Schema.Schema.Type<typeof OrderItem>;

const OrderDetailCoreFields = {
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  promotionRuleHints: Schema.Array(Schema.Unknown),
  customer: Schema.Union([Schema.Unknown, Schema.Null]),
  channel: Schema.Union([Schema.String.annotate({ format: 'iri-reference' }), Schema.Null]),
  shippingAddress: Schema.Union([Schema.Unknown, Schema.Null]),
  billingAddress: Schema.Union([Schema.Unknown, Schema.Null]),
  payments: Schema.Array(Schema.Unknown),
  shipments: Schema.Array(Schema.Unknown),
  currencyCode: Schema.String,
  localeCode: Schema.String,
  promotionCoupon: Schema.Union([Schema.Unknown, Schema.Null]),
  checkoutState: Schema.String.annotate({ default: 'cart' }),
  paymentState: Schema.String.annotate({ default: 'cart' }),
  shippingState: Schema.String.annotate({ default: 'cart' }),
  tokenValue: Schema.optionalKey(
    Schema.Union([Schema.String, Schema.Null]).annotate({ readOnly: true })
  ),
  checkoutCompletedAt: Schema.Union([DateTimeSerializableFromString, Schema.Null]),
  number: NullableString,
  notes: NullableString,
  items: Schema.Array(OrderItemBase),
  itemsTotal: ReadOnlyMoney,
  total: ReadOnlyMoney,
  state: Schema.String.annotate({ default: 'cart' }),
  totalWithoutTax: ReadOnlyMoney,
  itemsTotalWithoutTax: ReadOnlyMoney,
  orderPromotionTotalWithoutTax: ReadOnlyMoney,
  shippingWithoutTaxTotal: ReadOnlyMoney,
  paymentTotal: ReadOnlyMoney,
  paymentPromotionTotal: ReadOnlyMoney,
  paymentTax: ReadOnlyMoney,
  paymentWithoutTax: ReadOnlyMoney,
  vatFree: Schema.optionalKey(Schema.Boolean.annotate({ readOnly: true })),
  frontAllItemsPriceWithVatAfterDiscount: ReadOnlyNullableMoney,
  frontAllItemsPriceWithoutVatAfterDiscount: ReadOnlyMoney,
  frontAllItemsDiscountAsNominalValue: ReadOnlyMoney,
  frontAllItemsTaxAppliedAfterDiscount: ReadOnlyNullableMoney,
  frontTotalWithVat: ReadOnlyNullableMoney,
  frontTotalWithoutVat: ReadOnlyMoney,
  frontFinalRounding: ReadOnlyMoney,
  frontTotalTaxAppliedAfterDiscount: ReadOnlyNullableMoney,
  frontTotalPaid: ReadOnlyNullableMoney,
  frontShipmentTotalPaid: ReadOnlyNullableMoney,
  frontPaymentTotalPaid: ReadOnlyNullableMoney,
  frontDeliveryTotalPaid: ReadOnlyNullableMoney,
  frontShipmentWithVat: ReadOnlyNullableMoney,
  frontShipmentDiscountAsNominalValue: ReadOnlyMoney,
  frontShipmentWithVatAfterDiscount: ReadOnlyNullableMoney,
  frontShipmentWithoutVatAfterDiscount: ReadOnlyMoney,
  frontShipmentTaxAppliedAfterDiscount: ReadOnlyNullableMoney,
  frontPaymentWithVat: ReadOnlyNullableMoney,
  frontPaymentDiscountAsNominalValue: ReadOnlyMoney,
  frontPaymentWithVatAfterDiscount: ReadOnlyNullableMoney,
  frontPaymentWithoutVatAfterDiscount: ReadOnlyMoney,
  frontPaymentTaxAppliedAfterDiscount: ReadOnlyNullableMoney,
  frontDeliveryWithVat: ReadOnlyNullableMoney,
  frontDeliveryDiscountAsNominalValue: ReadOnlyMoney,
  frontDeliveryWithVatAfterDiscount: ReadOnlyNullableMoney,
  frontDeliveryWithoutVatAfterDiscount: ReadOnlyMoney,
  frontDeliveryTaxAppliedAfterDiscount: ReadOnlyNullableMoney,
  frontShipmentDiscountPercentage: ReadOnlyMoney,
  frontPaymentDiscountPercentage: ReadOnlyMoney,
  frontDeliveryDiscountPercentage: ReadOnlyMoney,
  orderPromotionTotal: ReadOnlyMoney,
  shippingPromotionTotal: ReadOnlyMoney,
  itemsSubtotal: ReadOnlyMoney,
  taxTotal: ReadOnlyMoney,
  shippingTaxTotal: ReadOnlyMoney,
  taxIncludedTotal: ReadOnlyMoney,
  shippingTotal: ReadOnlyMoney,
  invoiceUrl: Schema.optionalKey(NullableString.annotate({ readOnly: true })),
  globalState: Schema.optionalKey(
    Schema.Union([OrderGlobalStateValues, Schema.Null]).annotate({ readOnly: true })
  ),
};

export const OrderDetailBase = Schema.Struct(OrderDetailCoreFields);
export const OrderDetail = overrideSchema(OrderDetailBase, {
  '@id': OrderIri,
  items: Schema.Array(OrderItem),
  ...OrderReferenceOverrides,
  ...OrderLifecycleOverrides,
  itemsTotal: Schema.optionalKey(
    Schema.Finite.annotate({ readOnly: true })
      .check(Schema.isInt())
      .pipe(Schema.brand('Order.itemsTotal'))
  ),
  total: Schema.optionalKey(
    Schema.Finite.annotate({ readOnly: true })
      .check(Schema.isInt())
      .pipe(Schema.brand('Order.total'))
  ),
  totalWithoutTax: Schema.optionalKey(ReadOnlyMoney.pipe(Schema.brand('Order.totalWithoutTax'))),
  itemsTotalWithoutTax: Schema.optionalKey(
    ReadOnlyMoney.pipe(Schema.brand('Order.itemsTotalWithoutTax'))
  ),
  orderPromotionTotal: Schema.optionalKey(
    ReadOnlyMoney.pipe(Schema.brand('Order.orderPromotionTotal'))
  ),
  orderPromotionTotalWithoutTax: Schema.optionalKey(
    ReadOnlyMoney.pipe(Schema.brand('Order.orderPromotionTotalWithoutTax'))
  ),
  taxTotal: Schema.optionalKey(ReadOnlyMoney.pipe(Schema.brand('Order.taxTotal'))),
  frontAllItemsPriceWithVatAfterDiscount: Schema.optionalKey(
    ReadOnlyNullableMoney.pipe(Schema.brand('Order.frontAllItemsPriceWithVatAfterDiscount'))
  ),
  frontAllItemsPriceWithoutVatAfterDiscount: Schema.optionalKey(
    ReadOnlyMoney.pipe(Schema.brand('Order.frontAllItemsPriceWithoutVatAfterDiscount'))
  ),
  frontTotalWithVat: Schema.optionalKey(
    ReadOnlyNullableMoney.pipe(Schema.brand('Order.frontTotalWithVat'))
  ),
  frontTotalWithoutVat: Schema.optionalKey(
    ReadOnlyMoney.pipe(Schema.brand('Order.frontTotalWithoutVat'))
  ),
  frontTotalTaxAppliedAfterDiscount: Schema.optionalKey(
    ReadOnlyNullableMoney.pipe(Schema.brand('Order.frontTotalTaxAppliedAfterDiscount'))
  ),
  frontShipmentWithVat: Schema.optionalKey(
    ReadOnlyNullableMoney.pipe(Schema.brand('Order.frontShipmentWithVat'))
  ),
  frontShipmentDiscountAsNominalValue: Schema.optionalKey(
    ReadOnlyMoney.pipe(Schema.brand('Order.frontShipmentDiscountAsNominalValue'))
  ),
  frontShipmentWithoutVatAfterDiscount: Schema.optionalKey(
    ReadOnlyMoney.pipe(Schema.brand('Order.frontShipmentWithoutVatAfterDiscount'))
  ),
  frontPaymentWithVat: Schema.optionalKey(
    ReadOnlyNullableMoney.pipe(Schema.brand('Order.frontPaymentWithVat'))
  ),
  frontPaymentDiscountAsNominalValue: Schema.optionalKey(
    ReadOnlyMoney.pipe(Schema.brand('Order.frontPaymentDiscountAsNominalValue'))
  ),
  frontPaymentWithoutVatAfterDiscount: Schema.optionalKey(
    ReadOnlyMoney.pipe(Schema.brand('Order.frontPaymentWithoutVatAfterDiscount'))
  ),
}).annotate({ identifier: 'OrderDetail' });
export type OrderDetail = Schema.Schema.Type<typeof OrderDetail>;

export const OrderDetailPaidBase = Schema.Struct({
  ...OrderDetailCoreFields,
  invoiceId: NullableString,
  documents: Schema.optionalKey(Schema.Array(Schema.Unknown).annotate({ readOnly: true })),
});
export const OrderDetailPaid = overrideSchema(OrderDetailPaidBase, {
  '@id': OrderIri,
  ...OrderReferenceOverrides,
  invoiceId: Schema.Union([Schema.String.pipe(Schema.brand('Order.invoiceId')), Schema.Null]),
  ...OrderLifecycleOverrides,
}).annotate({ identifier: 'OrderDetailPaid' });
export type OrderDetailPaid = Schema.Schema.Type<typeof OrderDetailPaid>;

export const OrderIndexBase = Schema.Struct({
  '@id': JsonLdIri,
  '@type': JsonLdType,
  identifier: Schema.String.annotate({ format: 'uuid' }),
  invoiceId: NullableString,
  channel: Schema.Union([Schema.String.annotate({ format: 'iri-reference' }), Schema.Null]),
  shipments: Schema.Array(Schema.Unknown),
  checkoutState: Schema.String.annotate({ default: 'cart' }),
  paymentState: Schema.String.annotate({ default: 'cart' }),
  shippingState: Schema.String.annotate({ default: 'cart' }),
  tokenValue: Schema.optionalKey(
    Schema.Union([Schema.String, Schema.Null]).annotate({ readOnly: true })
  ),
  checkoutCompletedAt: Schema.Union([DateTimeSerializableFromString, Schema.Null]),
  number: NullableString,
  items: Schema.Array(Schema.Unknown),
  itemsTotal: ReadOnlyMoney,
  total: ReadOnlyMoney,
  state: Schema.String.annotate({ default: 'cart' }),
  totalWithoutTax: ReadOnlyMoney,
  itemsTotalWithoutTax: ReadOnlyMoney,
  shippingWithoutTaxTotal: ReadOnlyMoney,
  vatFree: Schema.optionalKey(Schema.Boolean.annotate({ readOnly: true })),
  itemsSubtotal: ReadOnlyMoney,
  taxTotal: ReadOnlyMoney,
  shippingTaxTotal: ReadOnlyMoney,
  taxIncludedTotal: ReadOnlyMoney,
  invoiceUrl: Schema.optionalKey(NullableString.annotate({ readOnly: true })),
  globalState: Schema.optionalKey(
    Schema.Union([OrderGlobalStateValues, Schema.Null]).annotate({ readOnly: true })
  ),
});
export const OrderIndex = overrideSchema(OrderIndexBase, {
  '@id': OrderIri,
  ...OrderReferenceOverrides,
  state: OrderState,
  globalState: OrderGlobalState,
}).annotate({ identifier: 'OrderIndex' });
export type OrderIndex = Schema.Schema.Type<typeof OrderIndex>;
